import React, { useState } from "react";
import Box from "@mui/material/Box";
import { ContactEngagementPanel } from "../../components/analytics/ContactEngagementPanel";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import LinearProgress from "@mui/material/LinearProgress";
import Skeleton from "@mui/material/Skeleton";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import TablePagination from "@mui/material/TablePagination";
import TableSortLabel from "@mui/material/TableSortLabel";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PeopleIcon from "@mui/icons-material/People";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import EmailIcon from "@mui/icons-material/Email";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SearchIcon from "@mui/icons-material/Search";
import {
  useAnalyticsOverview,
  useTeamMemberStats,
  useContactsAnalytics,
  useActivityTimeline,
} from "../../hooks/admin/use-analytics";
import type {
  TeamMemberStats,
  ContactAnalytics,
  ActivityEvent,
} from "@property-reports/api-contract";

// ============================================================================
// Utility Functions
// ============================================================================

function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return `${date.toLocaleDateString("en-NZ")} ${date.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })}`;
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-NZ");
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
}

function getMemberInitials(member: TeamMemberStats): string {
  if (member.name) {
    const parts = member.name.split(" ");
    return parts.length > 1
      ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
      : parts[0].substring(0, 2).toUpperCase();
  }
  return member.email.substring(0, 2).toUpperCase();
}

function getMemberFullName(member: TeamMemberStats): string {
  return member.name || member.email;
}

function getStatusColor(
  status: string,
): "success" | "warning" | "error" | "default" {
  switch (status) {
    case "active":
      return "success";
    case "paused":
      return "warning";
    case "unsubscribed":
      return "error";
    default:
      return "default";
  }
}

function getRoleBadgeColor(
  role: string,
): "primary" | "secondary" | "warning" | "default" {
  switch (role) {
    case "owner":
      return "warning";
    case "admin":
      return "primary";
    case "member":
      return "secondary";
    default:
      return "default";
  }
}

function getActivityIcon(type: string): string {
  switch (type) {
    case "report_sent":
      return "📧";
    case "contact_created":
      return "➕";
    case "contact_updated":
      return "✏️";
    case "contact_paused":
      return "⏸️";
    case "contact_activated":
      return "▶️";
    case "contact_unsubscribed":
      return "🚫";
    case "member_joined":
      return "👤";
    case "member_removed":
      return "👋";
    default:
      return "📝";
  }
}

function getActivityVerb(type: string): string {
  switch (type) {
    case "report_sent":
      return "sent report to";
    case "contact_created":
      return "added new contact";
    case "contact_updated":
      return "updated contact";
    case "contact_paused":
      return "paused contact";
    case "contact_activated":
      return "activated contact";
    case "contact_unsubscribed":
      return "unsubscribed";
    case "member_joined":
      return "joined the team";
    case "member_removed":
      return "left the team";
    default:
      return "updated";
  }
}

// ============================================================================
// Overview Cards Component
// ============================================================================

interface OverviewCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
  trend?: {
    value: number;
    label: string;
  };
}

function OverviewCard({
  title,
  value,
  subtitle,
  icon,
  color,
  loading,
  trend,
}: OverviewCardProps) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: `${color}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box sx={{ color }}>{icon}</Box>
          </Box>
          {trend && (
            <Chip
              size="small"
              icon={<TrendingUpIcon sx={{ fontSize: 14 }} />}
              label={`${trend.value > 0 ? "+" : ""}${trend.value}% ${trend.label}`}
              color={trend.value >= 0 ? "success" : "error"}
              variant="outlined"
              sx={{ height: 24 }}
            />
          )}
        </Box>
        <Typography variant="overline" color="text.secondary" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
        {loading ? (
          <Skeleton variant="text" width={80} height={40} />
        ) : (
          <Typography variant="h4" fontWeight={700} color="text.primary">
            {value}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Team Members Table with Expandable Rows
// ============================================================================

interface TeamMemberRowProps {
  member: TeamMemberStats;
}

function TeamMemberRow({ member }: TeamMemberRowProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          "&:hover": { backgroundColor: "action.hover" },
          cursor: "pointer",
        }}
        onClick={() => setOpen(!open)}
      >
        <TableCell>
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ bgcolor: "primary.main", width: 36, height: 36 }}>
              {getMemberInitials(member)}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {getMemberFullName(member)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {member.email}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={member.role.replace("_", " ")}
            color={getRoleBadgeColor(member.role)}
            variant="outlined"
          />
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={600}>
            {member.contactCount}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={600}>
            {member.reportsSentTotal}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" color="text.secondary">
            {member.reportsSentLast30Days}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {formatDateTime(member.lastActivityAt)}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={member.isActive ? "Active" : "Inactive"}
            color={member.isActive ? "success" : "default"}
            variant={member.isActive ? "filled" : "outlined"}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                color="text.secondary"
              >
                Member Details
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Total Contacts
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {member.contactCount}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Reports (All Time)
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {member.reportsSentTotal}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Reports (Last 30 Days)
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {member.reportsSentLast30Days}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Last Activity
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDateTime(member.lastActivityAt)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function TeamMembersTable() {
  const { data, isLoading } = useTeamMemberStats();

  if (isLoading) {
    return (
      <Card variant="outlined">
        <Box sx={{ p: 2 }}>
          <LinearProgress />
        </Box>
      </Card>
    );
  }

  const members = data?.members ?? [];

  return (
    <Card variant="outlined">
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50} />
              <TableCell>Member</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="right">Contacts</TableCell>
              <TableCell align="right">Reports Sent</TableCell>
              <TableCell align="right">Last 30 Days</TableCell>
              <TableCell>Last Activity</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    No team members found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              members.map((member) => (
                <TeamMemberRow key={member.id} member={member} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

// ============================================================================
// Contacts Table with Filtering
// ============================================================================

type SortByField =
  | "name"
  | "suburb"
  | "frequency"
  | "sendDay"
  | "reportCount"
  | "clicks"
  | "lastEngagedAt"
  | "lastSentAt"
  | "nextScheduledSend"
  | "status";

function ContactsTable() {
  const [filters, setFilters] = useState({
    ownerId: "",
    status: "",
    frequency: "",
    search: "",
  });
  const [page, setPage] = useState(0); // 0-indexed for MUI TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState<SortByField>("lastSentAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Helper to update filters and reset to first page
  const updateFilter = <K extends keyof typeof filters>(
    key: K,
    value: (typeof filters)[K],
  ) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(0);
  };

  const handleSort = (field: SortByField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(0);
  };

  const { data, isLoading } = useContactsAnalytics({
    ownerId: filters.ownerId || undefined,
    status: (filters.status || undefined) as
      | "active"
      | "paused"
      | "unsubscribed"
      | "all"
      | undefined,
    frequency: (filters.frequency || undefined) as
      | "weekly"
      | "fortnightly"
      | "monthly"
      | "all"
      | undefined,
    search: filters.search || undefined,
    sortBy,
    sortOrder,
    page: page + 1, // API is 1-indexed
    limit: rowsPerPage,
  });

  const contacts = data?.contacts ?? [];
  const total = data?.total ?? 0;

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search contacts..."
          value={filters.search}
          onChange={(e) => updateFilter("search", e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status}
            label="Status"
            onChange={(e) => updateFilter("status", e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="paused">Paused</MenuItem>
            <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Frequency</InputLabel>
          <Select
            value={filters.frequency}
            label="Frequency"
            onChange={(e) => updateFilter("frequency", e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="fortnightly">Fortnightly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <Card variant="outlined">
        {isLoading ? (
          <Box sx={{ p: 2 }}>
            <LinearProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "name"}
                        direction={sortBy === "name" ? sortOrder : "asc"}
                        onClick={() => handleSort("name")}
                      >
                        Contact
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "suburb"}
                        direction={sortBy === "suburb" ? sortOrder : "asc"}
                        onClick={() => handleSort("suburb")}
                      >
                        Suburb
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "frequency"}
                        direction={sortBy === "frequency" ? sortOrder : "asc"}
                        onClick={() => handleSort("frequency")}
                      >
                        Frequency
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "sendDay"}
                        direction={sortBy === "sendDay" ? sortOrder : "asc"}
                        onClick={() => handleSort("sendDay")}
                      >
                        Send Day
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === "reportCount"}
                        direction={sortBy === "reportCount" ? sortOrder : "asc"}
                        onClick={() => handleSort("reportCount")}
                      >
                        Reports
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === "clicks"}
                        direction={sortBy === "clicks" ? sortOrder : "asc"}
                        onClick={() => handleSort("clicks")}
                      >
                        Clicks
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "lastEngagedAt"}
                        direction={
                          sortBy === "lastEngagedAt" ? sortOrder : "asc"
                        }
                        onClick={() => handleSort("lastEngagedAt")}
                      >
                        Last Engaged
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "lastSentAt"}
                        direction={sortBy === "lastSentAt" ? sortOrder : "asc"}
                        onClick={() => handleSort("lastSentAt")}
                      >
                        Last Sent
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "nextScheduledSend"}
                        direction={
                          sortBy === "nextScheduledSend" ? sortOrder : "asc"
                        }
                        onClick={() => handleSort("nextScheduledSend")}
                      >
                        Next Scheduled
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "status"}
                        direction={sortBy === "status" ? sortOrder : "asc"}
                        onClick={() => handleSort("status")}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          py={4}
                        >
                          No contacts found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    contacts.map((contact) => (
                      <ContactRow key={contact.id} contact={contact} />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Card>
    </Box>
  );
}

interface ContactRowProps {
  contact: ContactAnalytics;
}

function ContactRow({ contact }: ContactRowProps) {
  const [open, setOpen] = React.useState(false);
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <>
      <TableRow hover sx={{ cursor: "pointer" }} onClick={() => setOpen(!open)}>
        <TableCell>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {contact.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {contact.email}
            </Typography>
            {contact.emailRecipients && contact.emailRecipients.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                {contact.emailRecipients
                  .filter((email) => email !== contact.email)
                  .map((email, idx) => (
                    <Typography
                      key={idx}
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      + {email}
                    </Typography>
                  ))}
              </Box>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{contact.owner.name}</Typography>
        </TableCell>
        <TableCell>
          <Box>
            <Typography variant="body2">{contact.suburb}</Typography>
            {contact.suburbSecondary && (
              <Typography variant="caption" color="text.secondary">
                + {contact.suburbSecondary}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={contact.frequency}
            variant="outlined"
            sx={{ textTransform: "capitalize" }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {dayNames[contact.sendDay] ?? "-"}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={600}>
            {contact.reportCount}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography
            variant="body2"
            color={contact.clicks > 0 ? "success.main" : "text.secondary"}
            fontWeight={contact.clicks > 0 ? 600 : 400}
          >
            {contact.clicks || "-"}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {formatDate(contact.lastEngagedAt)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {formatDate(contact.lastSentAt)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {formatDate(contact.nextScheduledSend)}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={contact.status}
            color={getStatusColor(contact.status)}
            variant={contact.status === "active" ? "filled" : "outlined"}
            sx={{ textTransform: "capitalize" }}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {open && <ContactEngagementPanel contactId={contact.id} />}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ============================================================================
// Activity Timeline Component
// ============================================================================

function ActivityTimeline() {
  const { data, isLoading } = useActivityTimeline({ limit: 20 });

  if (isLoading) {
    return (
      <Card variant="outlined">
        <Box sx={{ p: 2 }}>
          <LinearProgress />
        </Box>
      </Card>
    );
  }

  const events = data?.events ?? [];

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Recent Activity
        </Typography>
        {events.length === 0 ? (
          <Typography variant="body2" color="text.secondary" py={4}>
            No recent activity
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {events.map((event, index) => (
              <ActivityEventItem key={index} event={event} />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

interface ActivityEventItemProps {
  event: ActivityEvent;
}

function ActivityEventItem({ event }: ActivityEventItemProps) {
  const actorName = event.actor.name || event.actor.email;
  const targetName = event.target?.name;
  const targetSuburb = event.target?.suburb;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        p: 1.5,
        borderRadius: 1,
        backgroundColor: "grey.50",
        "&:hover": { backgroundColor: "grey.100" },
      }}
    >
      <Typography variant="h6" sx={{ lineHeight: 1 }}>
        {getActivityIcon(event.type)}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2">
          <Typography component="span" fontWeight={600}>
            {actorName}
          </Typography>{" "}
          {getActivityVerb(event.type)}
          {targetName && (
            <>
              {" "}
              <Typography component="span" fontWeight={500}>
                &quot;{targetName}&quot;
              </Typography>
            </>
          )}
          {targetSuburb && (
            <Typography component="span" color="text.secondary">
              {" "}
              ({targetSuburb})
            </Typography>
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatRelativeTime(event.timestamp)}
        </Typography>
      </Box>
    </Box>
  );
}

// ============================================================================
// Main TeamAnalytics Component
// ============================================================================

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export function TeamAnalytics() {
  const [tabValue, setTabValue] = useState(0);
  const { data: overview, isLoading: overviewLoading } = useAnalyticsOverview();

  // Calculate month-over-month change for reports
  const reportsTrend =
    overview && overview.reportsLastMonth > 0
      ? Math.round(
          ((overview.reportsThisMonth - overview.reportsLastMonth) /
            overview.reportsLastMonth) *
            100,
        )
      : undefined;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight={700} color="text.primary">
          Team Analytics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Monitor your team&apos;s activity, contacts, and report performance
        </Typography>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <OverviewCard
            title="Total Contacts"
            value={overview?.totalContacts ?? 0}
            subtitle={`${overview?.activeContacts ?? 0} active`}
            icon={<ContactMailIcon />}
            color="#1976d2"
            loading={overviewLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <OverviewCard
            title="Active Contacts"
            value={overview?.activeContacts ?? 0}
            subtitle={`${overview?.pausedContacts ?? 0} paused, ${overview?.unsubscribedContacts ?? 0} unsubscribed`}
            icon={<PeopleIcon />}
            color="#2e7d32"
            loading={overviewLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <OverviewCard
            title="Reports This Month"
            value={overview?.reportsThisMonth ?? 0}
            icon={<EmailIcon />}
            color="#ed6c02"
            loading={overviewLoading}
            trend={
              reportsTrend !== undefined
                ? { value: reportsTrend, label: "vs last month" }
                : undefined
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <OverviewCard
            title="Team Members"
            value={overview?.teamMemberCount ?? 0}
            subtitle={`Avg ${(overview?.avgReportsPerContact ?? 0).toFixed(1)} reports/contact`}
            icon={<PeopleIcon />}
            color="#9c27b0"
            loading={overviewLoading}
          />
        </Grid>
        {overview?.engagement && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <OverviewCard
                title="Read Rate"
                value={overview.engagement.openRate}
                subtitle={`${overview.engagement.totalOpened} of ${overview.engagement.totalDelivered || 0} emails read`}
                icon={<EmailIcon />}
                color="#1565c0"
                loading={overviewLoading}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <OverviewCard
                title="Click Rate"
                value={overview.engagement.clickRate}
                subtitle={`${overview.engagement.totalClicked} of ${overview.engagement.totalDelivered || 0} emails clicked`}
                icon={<EmailIcon />}
                color={
                  overview.engagement.status === "healthy"
                    ? "#2e7d32"
                    : overview.engagement.status === "warning"
                      ? "#ed6c02"
                      : "#d32f2f"
                }
                loading={overviewLoading}
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="Team Members" />
          <Tab label="All Contacts" />
          <Tab label="Activity" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <TeamMembersTable />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <ContactsTable />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <ActivityTimeline />
      </TabPanel>
    </Box>
  );
}
